import { BLOCK, CLIENT_EVENTS, AWAIT, LOCAL_ACTIONS } from "./agentContract";
import userDetail from "Stores/userDetail";
import { settingsStoreData } from "Stores/settingsData";
import onboardingCopilotStore from "Stores/onboardingCopilotStore";
import { getDomainFromEmail, isFreeMailDomain } from "Utils/emailDomain";
import { MXP_EVENT } from "Static/MixPanelEvents";
import * as svc from "./onboardingServices";

/**
 * The new-user copilot onboarding: a deterministic 10-step flow that turns a
 * signup into an activate-ready campaign, all inside the copilot drawer. Each
 * step is { id, expects(clientEvent, ctx) -> bool, run(api, clientEvent, ctx) }
 * driven by scriptedTransportBase; run() awaits the real service calls in
 * onboardingServices.js. ctx is onboardingCopilotStore.
 *
 * Copy rules (spec): no em-dashes, no exclamation-mark enthusiasm, numbers
 * over adjectives. Loaders are 4-5 rotating messages, never a bare spinner.
 * Onboarding never sends; activation stays on the sequence detail page.
 */

const firstName = () => userDetail?.userInfo?.firstName || "there";

const signupDomain = () => {
  const brand = userDetail?.brandUrlInfo?.domain || "";
  if (brand) return brand;
  const email =
    settingsStoreData?.userProfileData?.email || userDetail?.userInfo?.email || "";
  return getDomainFromEmail(email);
};

const track = (event, props = {}) => {
  try {
    if (window && window.mixpanel) window.mixpanel.track(event, props);
  } catch (e) {
    /* instrumentation is best-effort */
  }
};

const isCta = (evt, actionId) =>
  evt.type === CLIENT_EVENTS.CTA_CLICKED && evt.actionId === actionId;
const isReply = (evt, replyId) =>
  evt.type === CLIENT_EVENTS.QUICK_REPLY_SELECTED && evt.replyId === replyId;

// A 4-5 message rotating loader that runs `work` (a promise) to completion,
// then flips the block to done. Returns whatever `work` resolved to.
const runWork = async (api, turn, { label, messages, detail }, work) => {
  const blockId = turn.openBlock(BLOCK.PROGRESS, { status: "running", label, messages });
  try {
    const result = await work;
    turn.patch(blockId, { status: "done", detail });
    turn.closeBlock(blockId);
    return result;
  } catch (err) {
    turn.patch(blockId, { status: "failed", detail: "That step could not finish." });
    turn.closeBlock(blockId);
    throw err;
  }
};

const icpSummary = icp => {
  const bits = []
    .concat((icp.level || []).slice(0, 2), (icp.industry || []).slice(0, 2))
    .filter(Boolean);
  return bits.length ? bits.join(", ") : "your ideal customer profile";
};

// ── Step 0: welcome (emitted on connect, no client event) ────────────────
export const emitWelcome = async api => {
  const turn = api.beginTurn();
  onboardingCopilotStore.begin("onboarding", userDetail?.userInfo?.memberId);
  onboardingCopilotStore.setStep(0);
  track(MXP_EVENT.ONBOARDING_STARTED);
  await turn.text(
    `Welcome to Salesgear, ${firstName()}. I'm your Salesgear copilot. I can find your buyers, research them, and run your outreach with you. Let's set you up, it takes about two minutes.`
  );
  turn.addBlock(BLOCK.CTA_CHIPS, {
    chips: [
      { actionId: "ob_get_started", label: "Get started", style: "primary" },
      { actionId: "ob_dismiss", label: "I'll explore on my own" }
    ]
  });
  turn.end(AWAIT.CTA);
};

// ── Step 2-3: research the seller, save, capture ICP ─────────────────────
async function runProfileStep(api, ctx, website) {
  const turn = api.beginTurn();
  ctx.setStep(2);
  const domain = website || signupDomain();
  let draft;
  try {
    draft = await runWork(
      api,
      turn,
      {
        label: "Building your seller profile",
        messages: [
          `Reading ${domain}...`,
          "Understanding your product...",
          "Mapping your market...",
          "Identifying who buys this...",
          "Drafting your seller profile..."
        ],
        detail: "Seller profile drafted"
      },
      svc.enrichSeller(website)
    );
  } catch (err) {
    await turn.text(
      "I could not research that link just now. You can open the seller profile page and build it there, then come back and I'll pick up from your search."
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        {
          actionId: LOCAL_ACTIONS.NAVIGATE,
          path: "/settings/seller-profiles",
          label: "Open seller profile",
          style: "primary"
        }
      ]
    });
    turn.end(AWAIT.CTA);
    return;
  }
  ctx.patchArtifacts({ draftProfile: draft });
  await turn.text("Here's what I understood. Anything wrong? You can edit any card or just tell me.");
  turn.addBlock(BLOCK.SELLER_PROFILE_PREVIEW, { profile: draft, done: false });
  turn.end(AWAIT.SELLER_PROFILE_PREVIEW);
}

const ICP_QUESTIONS = derived => [
  {
    question: "Which seniority levels should I target?",
    header: "Seniority",
    multiSelect: true,
    options: [
      { label: "C-Suite" },
      { label: "VP" },
      { label: "Director" },
      { label: "Manager" }
    ]
  },
  {
    question: "What company sizes fit best?",
    header: "Company size",
    multiSelect: true,
    options: [
      { label: "1-50" },
      { label: "51-200" },
      { label: "201-1000" },
      { label: "1000+" }
    ]
  },
  {
    question: "Which regions should I search?",
    header: "Geography",
    multiSelect: true,
    options: [
      { label: "North America" },
      { label: "Europe" },
      { label: "Asia Pacific" },
      { label: "Global" }
    ]
  }
];

const LEVEL_MAP = { "C-Suite": "cxo", VP: "vp", Director: "director", Manager: "manager" };
const SIZE_MAP = {
  "1-50": "1-50",
  "51-200": "51-200",
  "201-1000": "201-1000",
  "1000+": "1001-10000"
};
const REGION_MAP = {
  "North America": ["United States", "Canada"],
  Europe: ["United Kingdom", "Germany", "France"],
  "Asia Pacific": ["India", "Australia", "Singapore"],
  Global: []
};

const answersToIcp = (answers, derived) => {
  const pick = header =>
    (answers.find(a => (a.question || "").toLowerCase().includes(header)) || {}).answer || "";
  const list = v => (Array.isArray(v) ? v : String(v || "").split(",").map(s => s.trim()).filter(Boolean));
  const levels = list(pick("seniority")).map(l => LEVEL_MAP[l]).filter(Boolean);
  const sizes = list(pick("size")).map(s => SIZE_MAP[s]).filter(Boolean);
  const regions = list(pick("region")).flatMap(r => REGION_MAP[r] || []);
  return {
    title: [],
    level: levels,
    industry: derived.industry || [],
    employeeCount: sizes,
    country: regions.length ? regions : derived.country || []
  };
};

// Step 1: what are you selling. Shared by the Get-started path and by resume,
// which drops a user back here rather than replaying the Step 0 welcome.
export const emitSellingQuestion = async (api, ctx) => {
  ctx.setStep(1);
  const domain = signupDomain();
  const turn = api.beginTurn();
  if (domain && !isFreeMailDomain(domain)) {
    await turn.text(
      `Let's quickly understand what you're selling. I found **${domain}** from your email. Is there a specific product on ${domain} you're selling, or should I use ${domain} itself?`
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: "ob_use_domain", label: `Use ${domain}`, style: "primary" },
        { actionId: "ob_specific_product", label: "It's a specific product" }
      ]
    });
    turn.end(AWAIT.CTA);
    return;
  }
  await turn.text(
    "Let's quickly understand what you're selling. Share the link that best describes your product and I'll research it."
  );
  turn.addBlock(BLOCK.WEBSITE_INPUT, {});
  turn.end(AWAIT.WEBSITE_INPUT);
};

// Resume: never restart from Step 0. Offer to pick up, then jump to the right
// point from the persisted artifacts.
export const emitResume = async api => {
  const turn = api.beginTurn();
  await turn.text(
    `Welcome back, ${firstName()}. Want to pick up where we left off?`
  );
  turn.addBlock(BLOCK.CTA_CHIPS, {
    chips: [
      { actionId: "ob_resume", label: "Pick up where we left off", style: "primary" },
      { actionId: "ob_restart", label: "Start over" }
    ]
  });
  turn.end(AWAIT.CTA);
};

// Step 3: who to reach. Shared by resume and by the off-script re-emit, which
// must return the user to the ICP form and not to the already-approved profile.
export const emitIcpForm = async (api, ctx) => {
  const a = ctx.artifacts || {};
  const derived = await svc.deriveIcp(a.draftProfile || {});
  ctx._icpDerived = derived;
  const turn = api.beginTurn();
  await turn.text("Let's define who to reach, then I'll search.");
  turn.addBlock(BLOCK.QUESTION_FORM, { questions: ICP_QUESTIONS(derived) });
  turn.end(AWAIT.QUESTION_FORM);
};

async function resumeFromArtifacts(api, ctx) {
  const a = ctx.artifacts || {};
  if (a.sequenceId) {
    const turn = api.beginTurn();
    await turn.text("Your campaign is already drafted. Open it to review and activate.");
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: LOCAL_ACTIONS.NAVIGATE, path: `/sequences/all/${a.sequenceId}`, label: "Review and activate", style: "primary" }
      ]
    });
    turn.end(AWAIT.CTA);
    ctx.complete();
    return;
  }
  if (a.listId) {
    return emitConnectStep(api, ctx);
  }
  if (a.savedProfileId || a.draftProfile) {
    return emitIcpForm(api, ctx);
  }
  // Past the welcome but nothing captured yet (the commonest drop-off point).
  // Resume at the question, never by replaying Step 0.
  return emitSellingQuestion(api, ctx);
}

// ── the scenario steps ────────────────────────────────────────────────────
const buildOnboardingScenario = () => [
  {
    id: "resume_pick",
    expects: evt => isCta(evt, "ob_resume"),
    run: async (api, evt, ctx) => resumeFromArtifacts(api, ctx)
  },
  {
    id: "resume_restart",
    expects: evt => isCta(evt, "ob_restart"),
    run: async (api, evt, ctx) => {
      ctx.status = "idle";
      ctx.begin("onboarding", userDetail?.userInfo?.memberId);
      return emitWelcome(api);
    }
  },
  // Step 1: entry / dismissal
  {
    id: "start_or_dismiss",
    expects: evt => isCta(evt, "ob_get_started") || isCta(evt, "ob_dismiss"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_dismiss") {
        ctx.dismiss();
        track(MXP_EVENT.ONBOARDING_DISMISSED, { at: "welcome" });
        const turn = api.beginTurn();
        await turn.text(
          "No problem. I'll be here in the copilot whenever you want to set things up."
        );
        turn.end(AWAIT.USER_INPUT);
        return;
      }
      return emitSellingQuestion(api, ctx);
    }
  },

  {
    id: "choose_product_source",
    expects: evt => isCta(evt, "ob_use_domain") || isCta(evt, "ob_specific_product"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_use_domain") {
        const domain = signupDomain();
        ctx.patchArtifacts({ website: domain });
        track(MXP_EVENT.ONBOARDING_DOMAIN_CONFIRMED, { source: "domain" });
        return runProfileStep(api, ctx, domain);
      }
      const turn = api.beginTurn();
      await turn.text("Paste the link that best describes what you're selling.");
      turn.addBlock(BLOCK.WEBSITE_INPUT, {});
      turn.end(AWAIT.WEBSITE_INPUT);
    }
  },

  {
    id: "website_submitted",
    expects: evt => evt.type === CLIENT_EVENTS.WEBSITE_SUBMITTED,
    run: async (api, evt, ctx) => {
      const website = (evt.website || "").trim();
      ctx.patchArtifacts({ website });
      track(MXP_EVENT.ONBOARDING_DOMAIN_CONFIRMED, { source: "link" });
      return runProfileStep(api, ctx, website);
    }
  },

  // Step 3: profile approved -> save + intelligence + ICP capture
  {
    id: "profile_approved",
    expects: evt => evt.type === CLIENT_EVENTS.SELLER_PROFILE_APPROVED,
    run: async (api, evt, ctx) => {
      const profile = evt.profile || ctx.artifacts.draftProfile || {};
      const turn = api.beginTurn();
      ctx.setStep(3);
      await turn.text(
        "Locking this in and generating your seller intelligence, the research layer I'll use to personalize every message you send."
      );
      const saved = await runWork(
        api,
        turn,
        {
          label: "Saving your seller intelligence",
          messages: [
            "Saving your profile...",
            "Building your research layer...",
            "Preparing your ideal customer profile..."
          ],
          detail: "Seller intelligence ready"
        },
        svc.saveSeller(profile)
      ).catch(() => null);
      ctx.patchArtifacts({ savedProfileId: (saved && saved.id) || "" });
      track(MXP_EVENT.ONBOARDING_PROFILE_CONFIRMED);
      const derived = await svc.deriveIcp(profile);
      ctx._icpDerived = derived;
      await turn.text(
        "Now let's define who to reach. I've made a start from your profile. Adjust anything, then I'll search."
      );
      turn.addBlock(BLOCK.QUESTION_FORM, { questions: ICP_QUESTIONS(derived) });
      turn.end(AWAIT.QUESTION_FORM);
    }
  },

  {
    id: "profile_discarded",
    expects: evt => evt.type === CLIENT_EVENTS.SELLER_PROFILE_DISCARDED,
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "No problem. Share a different link and I'll research it again, or open the seller profile page to build it yourself."
      );
      turn.addBlock(BLOCK.WEBSITE_INPUT, {});
      turn.end(AWAIT.WEBSITE_INPUT);
      return false; // allow another website submission to re-enter
    }
  },

  // Step 4-5: search + save list
  {
    id: "icp_submitted",
    expects: evt => evt.type === CLIENT_EVENTS.QUESTION_FORM_RESPONSE,
    run: async (api, evt, ctx) => {
      const derived = ctx._icpDerived || { industry: [], country: [] };
      const icp = answersToIcp(evt.answers || [], derived);
      ctx.patchArtifacts({ icpFilters: icp });
      ctx.setStep(4);
      const turn = api.beginTurn();
      await turn.text(`Now let's find your customers. Searching for people matching ${icpSummary(icp)}.`);
      let result = await runWork(
        api,
        turn,
        {
          label: "Searching prospects",
          messages: [
            "Matching titles to your profile...",
            "Filtering by company size...",
            "Checking regions...",
            "Ranking by fit..."
          ],
          detail: "Search complete"
        },
        svc.runSearch(icp)
      ).catch(() => ({ total: 0, rows: [] }));
      if ((result.total || 0) < 10) {
        const loosened = await svc.loosenSearch(icp).catch(() => null);
        if (loosened && loosened.dropped) {
          result = loosened;
          await turn.text(
            `That was a narrow match, so I loosened the ${loosened.dropped} filter to widen it.`
          );
        }
      }
      ctx.patchArtifacts({ searchTotal: result.total || 0 });
      track(MXP_EVENT.ONBOARDING_SEARCH_DONE, { total: result.total || 0 });
      const shown = Math.min((result.rows || []).length, 25);
      await turn.text(
        `Found **${(result.total || 0).toLocaleString()}** people matching your profile. Here are the top ${shown} to review.`
      );
      // The standard prospect table (opens the shared side panel as a popup in
      // the drawer). Served by the transport's fetchTablePage from the loaded
      // results, so it looks and behaves like the discover search.
      turn.addBlock(BLOCK.TABLE_REF, {
        tableId: "ob_prospects",
        title: "Matching decision makers",
        totalCount: shown,
        selectable: true,
        selectionScope: "page",
        // ContactActionBar denies every action unless the table names it, so
        // selection alone rendered a bar with nothing to do. Prospect rows are
        // not owned yet, so add-to-list is the only one that applies.
        actions: ["add_to_list"],
        columns: [
          { key: "name", label: "Name" },
          { key: "title", label: "Title" },
          { key: "company", label: "Company" },
          { key: "industry", label: "Industry" },
          { key: "location", label: "Location" }
        ]
      });
      turn.addBlock(BLOCK.CTA_CHIPS, {
        chips: [
          {
            actionId: LOCAL_ACTIONS.VIEW_TABLE,
            tableId: "ob_prospects",
            label: "View the list",
            icon: "grid_on"
          }
        ]
      });
      turn.addBlock(BLOCK.QUICK_REPLIES, {
        prompt: "Want me to save these as your first list?",
        replyId: "save_list",
        options: [
          { value: "save", label: "Add to list" },
          { value: "refine", label: "Refine the search first" }
        ]
      });
      turn.end(AWAIT.QUICK_REPLY);
    }
  },

  {
    id: "save_or_refine",
    expects: evt => isReply(evt, "save_list"),
    run: async (api, evt, ctx) => {
      if (evt.value === "refine") {
        const turn = api.beginTurn();
        await turn.text(
          "Open the list builder to refine titles and filters, then come back and I'll continue from your saved list."
        );
        turn.addBlock(BLOCK.CTA_CHIPS, {
          chips: [
            {
              actionId: LOCAL_ACTIONS.NAVIGATE,
              path: "/list-builder",
              label: "Open list builder",
              style: "primary"
            }
          ]
        });
        turn.end(AWAIT.CTA);
        return false; // stay available so a later save can proceed
      }
      ctx.setStep(5);
      const turn = api.beginTurn();
      const company = userDetail?.userInfo?.companyName || ctx.artifacts.website || "Your";
      const listName = `${company} - ICP prospects`;
      const saved = await runWork(
        api,
        turn,
        {
          label: "Saving your list",
          messages: ["Creating your list...", "Adding matched prospects...", "Finishing up..."],
          detail: "List saved"
        },
        svc.saveList(listName, null, ctx.artifacts.searchTotal)
      ).catch(() => null);
      const listSize = (saved && saved.listSize) || 0;
      ctx.patchArtifacts({
        listId: (saved && saved.listId) || "",
        listName: (saved && saved.listName) || listName,
        listSize
      });
      track(MXP_EVENT.ONBOARDING_LIST_SAVED);
      return emitEnrichStep(api, ctx, turn);
    }
  },

  // The user hand-picked prospects in the popup table and clicked "Add to
  // list". Add exactly those, then continue the flow into enrich, the same as
  // the scripted "Add to list" quick reply would.
  {
    id: "prospects_added_via_panel",
    expects: evt => evt.type === CLIENT_EVENTS.ADD_PROSPECTS_TO_LIST,
    run: async (api, evt, ctx) => {
      ctx.setStep(5);
      const rows = evt.rows || [];
      const result = await svc.addRowsToList(evt.listId, rows).catch(() => ({ added: 0, skipped: rows.length }));
      // Patch the table block so the shipped toast reports the real numbers.
      const patchTurn = api.beginTurn();
      if (evt.blockId) {
        patchTurn.patch(evt.blockId, {
          listResult: { added: result.added || 0, skipped: result.skipped || 0, listId: evt.listId }
        });
      }
      patchTurn.end(null);
      closeTablePopup();
      ctx.patchArtifacts({
        listId: evt.listId || "",
        listName: svc.getListName(evt.listId) || ctx.artifacts.listName || "",
        listSize: result.added || rows.length
      });
      track(MXP_EVENT.ONBOARDING_LIST_SAVED, { source: "panel" });
      const turn = api.beginTurn();
      await turn.text(
        `Added ${(result.added || rows.length).toLocaleString()} contacts to your list. Let's keep going.`
      );
      return emitEnrichStep(api, ctx, turn);
    }
  },

  // Step 6: enrich choice
  {
    id: "enrich_choice",
    expects: evt =>
      isCta(evt, "ob_enrich_email") || isCta(evt, "ob_enrich_both") || isCta(evt, "ob_enrich_skip"),
    run: async (api, evt, ctx) => {
      ctx.setStep(6);
      const turn = api.beginTurn();
      if (evt.actionId !== "ob_enrich_skip") {
        const type = evt.actionId === "ob_enrich_both" ? "EMAIL_PHONE" : "EMAIL";
        ctx.patchArtifacts({ enrichChoice: type });
        const res = await runWork(
          api,
          turn,
          {
            label: "Enriching contacts",
            messages: [
              "Finding verified emails...",
              type === "EMAIL_PHONE" ? "Finding direct dials..." : "Validating emails...",
              "Updating your list..."
            ],
            detail: "Enrichment started"
          },
          svc.enrichContacts(type, ctx.artifacts.listId)
        ).catch(() => ({ ok: false }));
        track(MXP_EVENT.ONBOARDING_ENRICHED, { type });
        await turn.text(
          res && res.ok
            ? "Your contacts are being enriched with verified details."
            : "Enrichment could not run just now. You can retry it from the list any time."
        );
      } else {
        await turn.text("No problem, we can enrich later from the list.");
      }
      return emitConnectStep(api, ctx);
    }
  },

  // Step 7: connect (loops on re-check, stays available)
  {
    id: "connect_check",
    expects: evt => isCta(evt, "ob_check_connection"),
    run: async (api, evt, ctx) => {
      const status = svc.connectStatus();
      ctx.patchArtifacts({ emailConnected: status.email, linkedInConnected: status.linkedIn });
      if (status.email) {
        track(MXP_EVENT.ONBOARDING_INBOX_CONNECTED);
        return emitCampaignStep(api, ctx);
      }
      const turn = api.beginTurn();
      await turn.text(
        "I don't see a connected inbox yet. Connect your email, then choose Check connection again."
      );
      turn.end(AWAIT.CTA);
      return false;
    }
  },

  {
    id: "connect_later",
    expects: evt => isCta(evt, "ob_connect_later"),
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "That's fine. I've saved your progress. When your inbox is connected, reopen the copilot and choose Check connection to build your first campaign."
      );
      turn.end(AWAIT.CTA);
    }
  },

  // Step 8: campaign style choice. The style cards themselves are the buttons
  // (COMPARISON_GRID cta), so the choice arrives as a user message.
  {
    id: "campaign_choice",
    expects: evt =>
      evt.type === CLIENT_EVENTS.USER_MESSAGE &&
      /create the .*campaign|connect linkedin to continue/i.test(evt.text || ""),
    run: async (api, evt, ctx) => {
      const text = (evt.text || "").toLowerCase();
      if (/connect linkedin/.test(text)) {
        const turn = api.beginTurn();
        await turn.text(
          "Connect LinkedIn from settings, then pick a LinkedIn style here."
        );
        turn.addBlock(BLOCK.CTA_CHIPS, {
          chips: [
            {
              actionId: LOCAL_ACTIONS.NAVIGATE,
              path: "/settings/account",
              label: "Connect LinkedIn",
              style: "primary"
            }
          ]
        });
        turn.end(AWAIT.USER_INPUT);
        return false; // stay available so a style can still be picked
      }
      const status = svc.connectStatus();
      if (!status.email) {
        const turn = api.beginTurn();
        await turn.text("Connect your email inbox first, then I can create the campaign.");
        turn.end(AWAIT.USER_INPUT);
        return false;
      }
      ctx.setStep(9);
      const deep = /deep research/.test(text);
      const linkedIn = /linkedin/.test(text);
      // Onboarding does not write the steps itself. Hand off to the shipped
      // "build a sequence with Copilot" flow, which reads the saved seller
      // profile, creates the sequence with steps, shows them, and offers open /
      // add-contacts against the saved list. Contacts still require a connected
      // inbox (Step 7 gate) before that flow can enrol them.
      const style = deep
        ? "a deep research email and LinkedIn sequence"
        : linkedIn
          ? "an email and LinkedIn sequence"
          : "a 3-step email sequence";
      track(MXP_EVENT.ONBOARDING_CAMPAIGN_CREATED, { deep, linkedIn });
      const turn = api.beginTurn();
      await turn.text(
        `Great choice. I'll build ${style} from your seller profile now. You can review the steps, then add your saved list once your inbox is connected.`
      );
      turn.end(AWAIT.USER_INPUT);
      ctx.complete();
      // Swap to the live copilot's build-sequence flow after this turn settles.
      handoffToSequenceBuilder(`${sequenceBrief(ctx)}Build ${style} from my seller profile`);
    }
  }
];

// Everything onboarding already asked the user, restated for the live
// sequence builder. Without it the build flow re-asked sequence type, audience
// and goal, and never learned about the list saved at Step 5.
function sequenceBrief(ctx) {
  const a = ctx.artifacts || {};
  const icp = a.icpFilters || {};
  const p = a.draftProfile || {};
  const bits = [];
  const join = v => (Array.isArray(v) ? v.filter(Boolean).join(", ") : "");
  const levels = join(icp.level);
  const sizes = join(icp.employeeCount);
  const regions = join(icp.country);
  const audience = [levels && `${levels} level`, sizes && `at ${sizes} employee companies`, regions && `in ${regions}`]
    .filter(Boolean)
    .join(" ");
  if (audience) bits.push(`Audience: ${audience}.`);
  const goal = String(p.goal_of_outreach || "").trim().replace(/[.\s]+$/, "");
  if (goal) bits.push(`Goal: ${goal}.`);
  if (a.listName) bits.push(`Enrol contacts from my list "${a.listName}".`);
  else if (a.listId) bits.push("Enrol the contacts from the list I just saved.");
  if (!bits.length) return "";
  return `I already answered these during setup, so use them and skip the setup questions. ${bits.join(" ")} `;
}

// Closes the shared prospect table popup (lazy require avoids a circular
// import: copilotStore -> AgentTransport -> OnboardingAgentTransport -> here).
function closeTablePopup() {
  try {
    // eslint-disable-next-line global-require
    require("Stores/copilotStore").default.closeTablePanel();
  } catch (e) {
    /* panel may already be closed */
  }
}

// Hands the campaign build to the live copilot's shipped build-sequence flow.
// Deferred a tick so the onboarding turn finishes rendering before the
// transport is swapped underneath it. Lazy require avoids the circular import.
function handoffToSequenceBuilder(triggerText) {
  setTimeout(() => {
    try {
      // eslint-disable-next-line global-require
      require("Stores/copilotStore").default.handoffToLiveSequenceBuilder(triggerText);
    } catch (e) {
      /* best-effort: the user can still open the sequence builder manually */
    }
  }, 400);
}

// Step 6 emitter (enrich, cost shown before any deduction). Reused by the
// scripted save and by the popup add-to-list path so both continue the same
// way. Writes into the turn it is given.
async function emitEnrichStep(api, ctx, turn) {
  turn = turn || api.beginTurn();
  // The popup add-to-list path knows the id but not the name, and the old
  // "your list" fallback rendered as "Your list your list is set up".
  const listName = ctx.artifacts.listName || "";
  const listSize = ctx.artifacts.listSize || 0;
  const credits = await svc.getCredits();
  const listPhrase = listSize ? `${listSize.toLocaleString()} contacts` : "your matched prospects";
  const costPhrase = listSize
    ? `Enriching ${listSize.toLocaleString()} contacts by email costs about ${listSize.toLocaleString()} credits.`
    : "Email enrichment costs 1 credit per contact.";
  await turn.text(
    `${listName ? `Your list **${listName}** is` : "Your list is"} set up with ${listPhrase}. Before we write to them, let's enrich the contacts with verified emails and phone numbers. It costs 1 credit per email and 10 per phone number. You have ${(credits.remaining || 0).toLocaleString()} credits. ${costPhrase}`
  );
  turn.addBlock(BLOCK.CTA_CHIPS, {
    chips: [
      { actionId: "ob_enrich_email", label: "Enrich emails", style: "primary" },
      { actionId: "ob_enrich_both", label: "Emails + phones" },
      { actionId: "ob_enrich_skip", label: "Skip for now" }
    ]
  });
  turn.end(AWAIT.CTA);
}

// Step 7 emitter, shared by the enrich step and resume.
function emitConnectStep(api, ctx) {
  ctx.setStep(7);
  const status = svc.connectStatus();
  ctx.patchArtifacts({ emailConnected: status.email, linkedInConnected: status.linkedIn });
  if (status.email) {
    return emitCampaignStep(api, ctx);
  }
  const turn = api.beginTurn();
  return turn
    .text(
      "All set on prospects. Let's connect your email and LinkedIn so we can get your campaign going. Campaigns start once your email inbox is connected."
    )
    .then(() => {
      turn.addBlock(BLOCK.CTA_CHIPS, {
        chips: [
          { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/settings/account", label: "Connect email", style: "primary" },
          { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/settings/account", label: "Connect LinkedIn" },
          { actionId: "ob_check_connection", label: "Check connection" },
          { actionId: "ob_connect_later", label: "I'll do this later" }
        ]
      });
      turn.end(AWAIT.CTA);
    });
}

// Step 8 emitter.
function emitCampaignStep(api, ctx) {
  ctx.setStep(8);
  const status = svc.connectStatus();
  const li = status.linkedIn;
  const turn = api.beginTurn();
  return turn.text("Your email is connected. Pick a campaign style and I'll set up the draft:").then(() => {
    // Each card carries its own button (ctaLabel + prompt), so the card is the
    // action. No separate chip row. LinkedIn styles stay visible but gated.
    // Subtitles describe the style, not a promise that steps are pre-written:
    // Step 8 hands off to the build-a-sequence flow, which writes the steps
    // from the seller profile, so the cards describe written steps again.
    turn.addBlock(BLOCK.COMPARISON_GRID, {
      title: "Campaign styles",
      options: [
        {
          title: "Email",
          subtitle: "3-step email sequence written from your seller profile",
          ctaLabel: "Use email",
          prompt: "Create the email campaign"
        },
        {
          title: "Email + LinkedIn",
          subtitle: li ? "Email plus LinkedIn touches" : "Connect LinkedIn to use this",
          ctaLabel: li ? "Use email and LinkedIn" : "Connect LinkedIn to use this",
          prompt: li ? "Create the email and LinkedIn campaign" : "Connect LinkedIn to continue"
        },
        {
          title: "Deep Research: Email + LinkedIn",
          subtitle: "Research each contact first, then write every step personally",
          recommended: true,
          ctaLabel: li ? "Use deep research" : "Connect LinkedIn to use this",
          prompt: li ? "Create the deep research campaign" : "Connect LinkedIn to continue"
        }
      ]
    });
    turn.end(AWAIT.USER_INPUT);
  });
}

// Re-shows the current step's prompt after an off-script interjection, so the
// flow returns to the rail. Re-derives from ctx (step + artifacts); never
// re-runs a mutating service call (steps 2/3 re-show the cached draft, not a
// fresh enrich).
export const emitCurrentStep = async (api, ctx) => {
  const step = ctx.step || 0;
  const a = ctx.artifacts || {};
  if (step <= 0) {
    return ctx.status === "active" && step > 0 ? emitResume(api) : emitWelcome(api);
  }
  if (step === 1) return emitSellingQuestion(api, ctx);
  if (step === 2) {
    // Only offer the draft again while it is genuinely unapproved. draftProfile
    // survives approval, so keying on it alone re-opened Approve/Discard for a
    // profile already saved.
    if (a.draftProfile && !a.savedProfileId) {
      const turn = api.beginTurn();
      await turn.text("Here's your seller profile. Review and edit it, or continue.");
      turn.addBlock(BLOCK.SELLER_PROFILE_PREVIEW, { profile: a.draftProfile, done: false });
      turn.end(AWAIT.SELLER_PROFILE_PREVIEW);
      return undefined;
    }
    if (a.savedProfileId || a.draftProfile) return emitIcpForm(api, ctx);
    return emitSellingQuestion(api, ctx);
  }
  if (step === 3) {
    if (a.savedProfileId || a.draftProfile) return emitIcpForm(api, ctx);
    return emitSellingQuestion(api, ctx);
  }
  if (step === 4 || step === 5) {
    const turn = api.beginTurn();
    turn.addBlock(BLOCK.QUICK_REPLIES, {
      prompt: "Want me to save these as your first list?",
      replyId: "save_list",
      options: [
        { value: "save", label: "Add to list" },
        { value: "refine", label: "Refine the search first" }
      ]
    });
    turn.end(AWAIT.QUICK_REPLY);
    return undefined;
  }
  if (step === 6) return emitEnrichStep(api, ctx);
  if (step === 7) return emitConnectStep(api, ctx);
  if (step === 8) return emitCampaignStep(api, ctx);
  return undefined; // step 9 / complete: nothing to re-emit
};

export default buildOnboardingScenario;
